import React, { type ChangeEvent, useContext } from 'react';
import { Editor, Node, Transforms, Element } from 'slate';
import { useSlateStatic, useReadOnly, ReactEditor } from 'slate-react';
import renderIf from 'render-if';
import classnames from 'classnames';
import ListContext from './ListContext';
import {
  getFirstTextChildMarks,
  getStyleFromChildMarks,
} from '../../../util/getStyleFromChildMarks';
import { isListItem } from '../guards';
import { shouldHideMarker } from '../utils';
import {
  type ComponentProps,
  type ListItemElement,
} from '../../../types/aha-editor';
import styles from './ListStyles.module.css';

const ListItem: React.FC<ComponentProps<ListItemElement>> = ({
  children,
  element,
  attributes,
}) => {
  const editor = useSlateStatic();
  const readOnly = useReadOnly();
  const type = useContext(ListContext);

  if (!isListItem(element)) return null;

  const style = getStyleFromChildMarks(element);
  const checked = element.list_checked;

  const onChecked = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const path = ReactEditor.findPath(editor, element);

    Transforms.setNodes(
      editor,
      { list_checked: event.target.checked },
      { at: path }
    );
  };

  const classesFromChildMarks = () => {
    return Object.keys(getFirstTextChildMarks(element))
      .map((e) => `${e}-child`)
      .join(' ');
  };

  const classesFromChildBlock = () => {
    const nodeEntry = Node.texts(element).next();
    if (!nodeEntry?.value) {
      return '';
    }

    const elementPath = ReactEditor.findPath(editor, element);
    const path = [...elementPath, ...nodeEntry.value[1]];

    const blockNodeEntry = Editor.above<Element>(editor, {
      at: path,
      match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
    });
    if (!blockNodeEntry) return '';

    return `${blockNodeEntry[0].type}-child`;
  };

  const innerAttributes = readOnly
    ? { disabled: true }
    : { onChange: onChecked };

  let Tag: 'li' | 'dt' | 'dd' = 'li';
  if (type === 'dl') {
    // Use the child index to alternate the tag.
    const path = ReactEditor.findPath(editor, element);
    if (path[path.length - 1] % 2 === 0) {
      Tag = 'dt';
    } else {
      Tag = 'dd';
    }
  }

  const noMarker = shouldHideMarker(element);
  const classes = classnames(
    {
      [styles.liCheckbox]: type === 'checklist',
      [styles.hideMarker]: noMarker,
    },
    classesFromChildMarks(),
    classesFromChildBlock()
  );
  if (type === 'checklist' && Array.isArray(children)) {
    const [paragraph, ...otherChildren] = React.Children.toArray(children);
    return (
      <Tag className={classes} style={style} {...attributes}>
        {renderIf(type === 'checklist')(() => (
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
            }}
          >
            {renderIf(!noMarker)(() => (
              <span
                className={styles.checkboxContainer}
                contentEditable={false}
              >
                <input
                  type="checkbox"
                  checked={!!checked}
                  {...innerAttributes}
                />
              </span>
            ))}
            {paragraph}
          </div>
        ))}
        {...otherChildren}
      </Tag>
    );
  } else {
    return (
      <Tag className={classes} style={style} {...attributes}>
        {children}
      </Tag>
    );
  }
};

export default ListItem;
